import React, { useState, useRef, useEffect } from "react";
import { Modal, Button, Input, List, Tag } from "antd";
import { GoogleMap, Marker } from "@react-google-maps/api";
import { DeleteOutlined } from "@ant-design/icons";

const containerStyle = { width: "100%", height: "420px" };

/**
 * CampMapPicker
 *
 * - Click on map (when drawing) to drop numbered markers
 * - Each marker is draggable — drag to adjust position
 * - Delete individual points from the list below the map
 * - No polygon connecting the points
 * - Location search to pan/jump to a place (does NOT auto-add a point)
 *
 * Props:
 *   isOpen        {boolean}
 *   onClose       {() => void}
 *   onSave        {(points: [{lat, lng}]) => void}
 *   initialPoints {[{lat, lng}]}   optional — for edit mode
 */
const CampMapPicker = ({ isOpen, onClose, onSave, initialPoints = [] }) => {
  const [points, setPoints] = useState([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentCenter, setCurrentCenter] = useState({ lat: 19.0859, lng: 72.9076 });
  const [searchText, setSearchText] = useState("");
  const [suggestions, setSuggestions] = useState([]);

  const autocompleteServiceRef = useRef(null);
  const placesServiceRef = useRef(null);
  const debounceRef = useRef(null);

  // ── reset on open ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (!window.google) return;
    if (!autocompleteServiceRef.current) {
      autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
    }
    if (isOpen) {
      const initial = initialPoints.length > 0 ? initialPoints : [];
      setPoints(initial);
      setIsDrawing(false);
      setSearchText("");
      setSuggestions([]);
      if (initial.length > 0) {
        setCurrentCenter(initial[0]);
      } else {
        setCurrentCenter({ lat: 19.0859, lng: 72.9076 });
      }
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── map click — add point only when drawing ─────────────────────────────────
  const handleMapClick = (e) => {
    if (!isDrawing) return;
    const newPoint = { lat: e.latLng.lat(), lng: e.latLng.lng() };
    setPoints((prev) => [...prev, newPoint]);
  };

  // ── marker drag end — update that point's position ──────────────────────────
  const handleMarkerDragEnd = (index, e) => {
    const updated = { lat: e.latLng.lat(), lng: e.latLng.lng() };
    setPoints((prev) => prev.map((p, i) => (i === index ? updated : p)));
  };

  // ── delete a point by index ──────────────────────────────────────────────────
  const handleDeletePoint = (index) => {
    setPoints((prev) => prev.filter((_, i) => i !== index));
  };

  // ── location search ──────────────────────────────────────────────────────────
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchText(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!value) { setSuggestions([]); return; }
    debounceRef.current = setTimeout(() => {
      autocompleteServiceRef.current?.getPlacePredictions(
        { input: value },
        (predictions, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
            setSuggestions(predictions);
          } else {
            setSuggestions([]);
          }
        }
      );
    }, 800);
  };

  const handleSuggestionClick = (placeId) => {
    if (!placesServiceRef.current) {
      placesServiceRef.current = new window.google.maps.places.PlacesService(
        document.createElement("div")
      );
    }
    placesServiceRef.current.getDetails({ placeId }, (place, status) => {
      if (status === window.google.maps.places.PlacesServiceStatus.OK && place.geometry) {
        setCurrentCenter({
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
        });
        setSearchText(place.formatted_address || "");
        setSuggestions([]);
      }
    });
  };

  // ── save / cancel ────────────────────────────────────────────────────────────
  const handleSave = () => {
    onSave(points);
    setPoints([]);
    setSearchText("");
    setIsDrawing(false);
  };

  const handleCancel = () => {
    setPoints([]);
    setSearchText("");
    setSuggestions([]);
    setIsDrawing(false);
    onClose();
  };

  // ── render ───────────────────────────────────────────────────────────────────
  return (
    <Modal
      title={<span style={{ fontWeight: 600 }}>Pick Camp Coordinates</span>}
      open={isOpen}
      onCancel={handleCancel}
      width={1000}
      footer={[
        <Button key="cancel" onClick={handleCancel}>Cancel</Button>,
        <Button
          key="save"
          type="primary"
          disabled={points.length === 0}
          onClick={handleSave}
        >
          Save Coordinates ({points.length} pts)
        </Button>,
      ]}
    >
      {/* Search bar — pans the map, does NOT add a point */}
      {/* <div style={{ marginBottom: 16, position: "relative" }}>
        <Input
          placeholder="Search location to pan map..."
          value={searchText}
          onChange={handleSearchChange}
        />
        {suggestions.length > 0 && (
          <List
            bordered
            style={{
              position: "absolute",
              top: 42,
              width: "100%",
              zIndex: 1000,
              background: "#fff",
              maxHeight: 200,
              overflowY: "auto",
            }}
            dataSource={suggestions}
            renderItem={(item) => (
              <List.Item
                key={item.place_id}
                onClick={() => handleSuggestionClick(item.place_id)}
                style={{ cursor: "pointer" }}
              >
                {item.description}
              </List.Item>
            )}
          />
        )}
      </div> */}

      {/* Drawing controls */}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, marginBottom: 12 }}>
        <Tag color={isDrawing ? "green" : "default"} style={{ lineHeight: "32px", padding: "0 12px" }}>
          {isDrawing ? "Drawing ON — click map to add points" : "Drawing OFF"}
        </Tag>
        <Button
          type={isDrawing ? "default" : "primary"}
          onClick={() => setIsDrawing((prev) => !prev)}
        >
          {isDrawing ? "Stop Drawing" : "Start Drawing"}
        </Button>
        <Button
          danger
          disabled={points.length === 0}
          onClick={() => setPoints([])}
        >
          Clear All
        </Button>
      </div>

      {/* Google Map */}
      <GoogleMap
        mapContainerStyle={containerStyle}
        center={currentCenter}
        zoom={10}
        onClick={handleMapClick}
      >
        {points.map((point, index) => (
          <Marker
            key={index}
            position={point}
            draggable={true}
            onDragEnd={(e) => handleMarkerDragEnd(index, e)}
            label={{
              text: String(index + 1),
              color: "#fff",
              fontWeight: "bold",
              fontSize: "12px",
            }}
          />
        ))}
      </GoogleMap>

      {/* Points list with delete buttons */}
      {points.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <h4 style={{ fontWeight: 600, marginBottom: 8 }}>
            Selected Points ({points.length}):
          </h4>
          <div
            style={{
              maxHeight: 160,
              overflowY: "auto",
              border: "1px solid #f0f0f0",
              borderRadius: 6,
            }}
          >
            {points.map((point, index) => (
              <div
                key={index}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "6px 12px",
                  borderBottom: index < points.length - 1 ? "1px solid #f0f0f0" : "none",
                  fontSize: 13,
                }}
              >
                <span>
                  <Tag color="blue" style={{ marginRight: 8 }}>{index + 1}</Tag>
                  Lat: <strong>{point.lat.toFixed(6)}</strong>
                  &nbsp;&nbsp;Lng: <strong>{point.lng.toFixed(6)}</strong>
                </span>
                <Button
                  type="text"
                  danger
                  size="small"
                  icon={<DeleteOutlined />}
                  onClick={() => handleDeletePoint(index)}
                />
              </div>
            ))}
          </div>
          <p style={{ fontSize: 12, color: "#888", marginTop: 6 }}>
            Drag any marker on the map to adjust its position.
          </p>
        </div>
      )}
    </Modal>
  );
};

export default CampMapPicker;